'use client';

import { useEffect, useRef } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ANALYTICS_EVENTS } from '@/lib/analytics/events';
import { trackEvent } from '@/lib/analytics/track';
import type { AnalyticsSurface } from '@/lib/analytics/types';

interface ErrorScreenProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  opportunityId?: string;
  surface?: AnalyticsSurface;
}

export function ErrorScreen({
  title = 'Something went wrong',
  message,
  onRetry,
  opportunityId,
  surface = 'client_flow',
}: ErrorScreenProps) {
  const trackedRef = useRef(false);

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackEvent({
      event: ANALYTICS_EVENTS.error_screen_shown,
      properties: { title, surface, opportunityId },
      context: {
        actor: surface.startsWith('rep') ? 'rep' : 'client',
        surface,
        opportunityId,
      },
    });
  }, [title, message, surface, opportunityId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
          <p className="text-gray-600 mb-6">{message}</p>
          {onRetry && (
            <Button onClick={onRetry} variant="default">
              Try Again
            </Button>
          )}
          <p className="mt-4 text-sm text-gray-500">
            Need help?{' '}
            <a href="tel:8883774074" className="text-primary hover:underline">
              Call (888) 377-4074
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
